/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from "react"
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Table from 'react-bootstrap/Table'

import AdministrationTabs from '../components/AdministrationTabs.jsx'
import DebugAccordion from "../../../components/DebugAccordion.jsx"
import Icon from "../../../components/Icon.jsx"
import NewUserModal from "./components/NewUserModal.jsx"
import PageTitle from "../../../components/PageTitle.jsx"

import { NotificationTypes, addNotification } from "../../../state/actions/notificationActions"
import { fetchUserList } from "../../../state/actions/userActions.js"

import { userListSignal, userListLoadedSignal, userListErrorSignal } from "../../../state/signals/userSignals.js"

import { SortDirectionType, UserRoles } from "../../../utils/constants.js"
import { computeSortedData } from '../../../utils/table.js'
import LoadingAnimation from "../../../components/LoadingAnimation.jsx"
import PageSizeSelector from "../../../components/PageSizeSelector.jsx"
import CopyRowAsJSON from "../../../components/CopyRowAsJSON.jsx"

export default function UserList() {

    const { beId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUsers
        }
    )

    const navigate = useNavigate()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUsers) {
            addNotification('You are not authorized to view users for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditUsers
    })

    const showModalNewUserSignal = useSignal(false)

    const currentPageSignal = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize'), 10) || 250)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')

    useEffect(() => {
        fetchUserList(beId, config, accessToken)
    }, [beId, config, accessToken])

    const sortDirection = useSignal(SortDirectionType.DESCENDING)

    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const filteredData = useComputed(() => {
        if (!filterStringSignal.value) {
            return userListSignal.value ? [...userListSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return userListSignal.value.filter(item => {
            // Limit search to these fields
            let searchData = {
                username: item.username,
                firstname: item.firstName,
                lastname: item.lastName,
                mmail: item.email,
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedData = useComputed(computeSortedData(filteredData, sortColumnSignal, sortDirection))

    const totalPages = useComputed(() => {
        return Math.ceil(filteredData.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayData = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedData.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumber = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumber = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredData.value.length ? endIndex : filteredData.value.length
    })

    const accordionData = [
        { name: 'Users', data: userListSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]


    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }

    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirection.value = sortDirection.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumnSignal signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPages.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableRows({ items }) {
        if (!userListLoadedSignal.value) {
            return (
                <tr><td colSpan={10} className="text-center"><LoadingAnimation /></td></tr>
            )
        }
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })
            return rows
        }
        return (
            <tr><td colSpan="10" className="text-center">No users found</td></tr>
        )
    }

    // const getUserRoleString = function (type) {
    //     switch (type) {
    //         case UserRoles.ReadOnly:
    //             return "Read Only"
    //         case UserRoles.Manager:
    //             return "Manager"
    //         case UserRoles.TechnicalAdvisor:
    //             return "Technical Advisor"
    //         case UserRoles.Technician:
    //             return "Technician"
    //         case UserRoles.HelpDesk:
    //             return "Help Desk Administrator"
    //         default:
    //             return "Error"
    //     }
    // }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" width="16" height="16"></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {
                                canEditComputed.value ? (
                                    <Dropdown.Item as={Link} to={`../users/${item.id}`}>
                                        <Icon icon="pencil" height={16} width={16} />
                                        &nbsp;Edit Profile
                                    </Dropdown.Item>
                                ) : (
                                        <Dropdown.Item>
                                            <Icon icon="no-pencil" height={16} width={16} />
                                            <span className="text-muted">&nbsp;Can&apos;t Edit</span>
                                        </Dropdown.Item>
                                )
                            }
                            <CopyRowAsJSON item={item} field="email" />
                        </Dropdown.Menu>
                    </Dropdown>
                </td>

                <td><Link to={`../users/${item.id}`}>{item.username}</Link></td>
                <td>{item.firstName}</td>
                <td>{item.lastName}</td>
                <td>{item.email}</td>
                <td>{item.emailAddressVerified ? 'Yes' : 'No'}</td>

                {/* <td>{getUserRoleString(item.userType)}</td> */}
                <td>{item.isActive ? 'Yes' : 'No'}</td>

                <td>{item.isLocked ? 'Yes' : 'No'}</td>
                <td>{(!item.isInitialUser) ? 'Yes' : 'No'}</td>
            </tr>
        )
    }

    const showNewUserModal = function () {
        //console.log("Showing the new user modal")
        showModalNewUserSignal.value = true
        //console.log("You should see it now")
    }

    if (!userListLoadedSignal.value) {
        return (<LoadingAnimation />)
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
                <Breadcrumb.Item active>Users</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='Users' canEditSignal={canEditSignal} />
            <AdministrationTabs activeKey="4"></AdministrationTabs>
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item className="me-auto">
                    <PageSizeSelector pageSizeSignal={rowsPerPageSignal} handlePageSizeSelect={handlePageSizeSelect} />
                </Nav.Item>
                <Nav.Item>
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                {canEditSignal.value && <Nav.Item className="ms-2">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={showNewUserModal}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;Create User</Button>
                </Nav.Item>}
            </Nav>

            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Username'} sortable={true} datafield={'username'}></TableColumnHeader>
                        <TableColumnHeader title={'First Name'} sortable={true} datafield={'firstName'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Name'} sortable={true} datafield={'lastName'}></TableColumnHeader>
                        <TableColumnHeader title={'Email Address'} sortable={true} datafield={'email'}></TableColumnHeader>
                        <TableColumnHeader title={'Email Verified'} sortable={true} datafield={'emailAddressVerified'}></TableColumnHeader>

                        {/* <TableColumnHeader title={'Role'} sortable={true} datafield={'userType'}></TableColumnHeader> */}
                        <TableColumnHeader title={'Active'} sortable={true} datafield={'isActive'}></TableColumnHeader>

                        <TableColumnHeader title={'Account Locked'} sortable={true} datafield={'isLocked'}></TableColumnHeader>
                        <TableColumnHeader title={'Has Logged In'} sortable={true} datafield={'isInitialUser'}></TableColumnHeader>
                    </tr>
                </thead>
                <tbody><TableRows items={displayData.value} /></tbody>
                <tfoot><tr><td className="fs-6" colSpan={10}>Displaying {startDisplayRowNumber.value}-{endDisplayRowNumber.value} of {filteredData.value.length}</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />
            <NewUserModal
                showSignal={showModalNewUserSignal}
            ></NewUserModal>
        </>
    )
}